import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Carrot, Fish, Beef, Leaf, MapPin, ChevronRight, BarChart3, Sprout, Thermometer, Factory, CheckCircle2, ArrowRight } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Tooltip } from '../ui/Tooltip';
const ingredients = [{
  id: 'chicken',
  name: 'Pollo de Corral',
  role: 'Proteína Magra',
  description: 'Criado en libertad en granjas de Galicia. Fuente principal de aminoácidos esenciales para el desarrollo muscular magro.',
  origin: 'Galicia, España',
  certifications: ['Bienestar Animal', 'Km 0'],
  image: 'https://images.unsplash.com/photo-1587593810167-a84920ea0781?auto=format&fit=crop&q=80&w=800',
  benefits: ['Alto valor biológico', 'Bajo en grasa saturada', 'Rico en Vitamina B12'],
  nutrients: {
    protein: 85,
    fat: 10,
    fiber: 5,
    vitamins: 'B12, B6'
  },
  processing: 'Cocción lenta a 80°C',
  comparison: 'vs Harina de Pollo (Industrial)',
  icon: Beef,
  color: 'bg-orange-100 text-orange-600',
  accent: 'border-orange-200'
}, {
  id: 'salmon',
  name: 'Salmón Atlántico',
  role: 'Omega 3 & 6',
  description: 'Pesca sostenible del Atlántico Norte. Fundamental para la salud cardiovascular, el desarrollo cognitivo y el brillo del pelaje.',
  origin: 'Atlántico Norte',
  certifications: ['MSC Sostenible', 'Fresco'],
  image: 'https://images.unsplash.com/photo-1599084993091-1cb5c0721cc6?auto=format&fit=crop&q=80&w=800',
  benefits: ['Antiinflamatorio natural', 'Salud cerebral', 'Piel sana'],
  nutrients: {
    protein: 70,
    fat: 25,
    fiber: 5,
    vitamins: 'D, B12'
  },
  processing: 'Prensado en frío',
  comparison: 'vs Aceite Refinado',
  icon: Fish,
  color: 'bg-blue-100 text-blue-600',
  accent: 'border-blue-200'
}, {
  id: 'carrot',
  name: 'Zanahorias',
  role: 'Fibra & Visión',
  description: 'Frescas de la huerta valenciana. Aportan betacarotenos esenciales para la salud ocular y fibra prebiótica para la digestión.',
  origin: 'Valencia, España',
  certifications: ['Orgánico', 'Local'],
  image: 'https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?auto=format&fit=crop&q=80&w=800',
  benefits: ['Vitamina A (Retinol)', 'Antioxidantes', 'Salud dental'],
  nutrients: {
    protein: 10,
    fat: 5,
    fiber: 85,
    vitamins: 'A, K'
  },
  processing: 'Vapor suave',
  comparison: 'vs Pulpa de Remolacha',
  icon: Carrot,
  color: 'bg-orange-100 text-orange-600',
  accent: 'border-orange-200'
}, {
  id: 'spinach',
  name: 'Espinacas Baby',
  role: 'Superalimento',
  description: 'Hojas tiernas ricas en hierro y antioxidantes. Fortalecen el sistema inmunológico y combaten el envejecimiento celular.',
  origin: 'Navarra, España',
  certifications: ['Eco-Friendly', 'Sin Pesticidas'],
  image: 'https://images.unsplash.com/photo-1576045057995-568f588f82fb?auto=format&fit=crop&q=80&w=800',
  benefits: ['Hierro biodisponible', 'Vitamina K', 'Ácido fólico'],
  nutrients: {
    protein: 30,
    fat: 5,
    fiber: 65,
    vitamins: 'K, A, C'
  },
  processing: 'Blanqueado rápido',
  comparison: 'vs Rellenos de Cereal',
  icon: Leaf,
  color: 'bg-green-100 text-green-600',
  accent: 'border-green-200'
}];
export function IngredientsShowcase() {
  const [activeIngredient, setActiveIngredient] = useState(ingredients[0]);
  return <section className="py-24 lg:py-32 bg-white relative overflow-hidden" data-id="element-2222">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30" data-id="element-2223" />

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-2224">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2225">
          <EnhancedScrollReveal data-id="element-2226">
            <Badge variant="secondary" className="mb-6 bg-green-100 text-green-700 border-green-200" data-id="element-2227">
              <Sprout className="w-3 h-3 mr-1" data-id="element-2228" /> Ingredientes Reales
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6" data-id="element-2229">
              Tu despensa, <br data-id="element-2230" />
              <span className="text-green-600" data-id="element-2231">ahora es la suya</span>
            </h2>
            <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-2232">
              Sin subproductos, sin harinas, sin secretos. Solo ingredientes
              enteros que podrías comprar en tu mercado local.
            </p>
          </EnhancedScrollReveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-2233">
          {/* Left: Ingredient Spotlight */}
          <div className="lg:col-span-7" data-id="element-2234">
            <EnhancedScrollReveal data-id="element-2235">
              <div className="relative bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl border border-gray-100 overflow-hidden min-h-[650px] flex flex-col justify-between" data-id="element-2236">
                <AnimatePresence mode="wait" data-id="element-2237">
                  <motion.div key={activeIngredient.id} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.4
                }} className="relative z-10 h-full flex flex-col" data-id="element-2238">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6" data-id="element-2239">
                      <div data-id="element-2240">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${activeIngredient.color} bg-opacity-20 text-xs font-bold uppercase tracking-wider mb-3`} data-id="element-2241">
                          <activeIngredient.icon className="w-4 h-4" data-id="element-2242" />
                          {activeIngredient.role}
                        </div>
                        <h3 className="text-4xl font-serif font-bold text-charcoal-900 mb-2" data-id="element-2243">
                          {activeIngredient.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm font-medium" data-id="element-2244">
                          <div className="flex items-center gap-1.5 text-gray-500" data-id="element-2245">
                            <MapPin className="w-4 h-4 text-primary-500" data-id="element-2246" />{' '}
                            {activeIngredient.origin}
                          </div>
                          <div className="h-4 w-px bg-gray-200" data-id="element-2247" />
                          <div className="flex gap-2" data-id="element-2248">
                            {activeIngredient.certifications.map((cert, i) => <Badge key={i} variant="outline" size="sm" className="text-xs border-gray-200 text-gray-600" data-id="element-2249">
                                {cert}
                              </Badge>)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Image */}
                    <div className="relative aspect-video rounded-3xl overflow-hidden mb-8 shadow-lg group" data-id="element-2250">
                      <img src={activeIngredient.image} alt={activeIngredient.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-2251" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" data-id="element-2252" />

                      {/* Processing Tag */}
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold text-charcoal-800 flex items-center gap-1.5 shadow-sm" data-id="element-2253">
                        <Thermometer className="w-3.5 h-3.5 text-orange-500" data-id="element-2254" />
                        {activeIngredient.processing}
                      </div>

                      <div className="absolute bottom-6 left-6 text-white max-w-lg" data-id="element-2255">
                        <p className="font-medium text-lg leading-relaxed mb-2" data-id="element-2256">
                          "{activeIngredient.description}"
                        </p>
                        <div className="flex items-center gap-2 text-xs font-medium text-white/80 bg-white/10 backdrop-blur px-3 py-1 rounded-lg w-fit" data-id="element-2257">
                          <Factory className="w-3 h-3" data-id="element-2258" />
                          Mejor que: {activeIngredient.comparison}
                        </div>
                      </div>
                    </div>

                    {/* Stats & Benefits */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-auto" data-id="element-2259">
                      <div data-id="element-2260">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2261">
                          <CheckCircle2 className="w-4 h-4" data-id="element-2262" /> Beneficios Clave
                        </h4>
                        <ul className="space-y-3" data-id="element-2263">
                          {activeIngredient.benefits.map((benefit, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-charcoal-700 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-2264">
                              <div className={`w-2 h-2 rounded-full ${activeIngredient.color.split(' ')[1].replace('text', 'bg')}`} data-id="element-2265" />
                              {benefit}
                            </li>)}
                        </ul>
                      </div>

                      <div className="bg-charcoal-900 rounded-2xl p-6 text-white shadow-lg" data-id="element-2266">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2267">
                          <BarChart3 className="w-4 h-4" data-id="element-2268" /> Perfil Nutricional
                        </h4>
                        <div className="space-y-4" data-id="element-2269">
                          <div className="space-y-1.5" data-id="element-2270">
                            <div className="flex justify-between text-xs font-medium" data-id="element-2271">
                              <span className="text-gray-300" data-id="element-2272">Proteína</span>
                              <span className="text-white font-bold" data-id="element-2273">
                                {activeIngredient.nutrients.protein}%
                              </span>
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-2274">
                              <motion.div initial={{
                              width: 0
                            }} animate={{
                              width: `${activeIngredient.nutrients.protein}%`
                            }} transition={{
                              duration: 1,
                              delay: 0.2
                            }} className="h-full bg-emerald-400 rounded-full" data-id="element-2275" />
                            </div>
                          </div>

                          <div className="space-y-1.5" data-id="element-2276">
                            <div className="flex justify-between text-xs font-medium" data-id="element-2277">
                              <span className="text-gray-300" data-id="element-2278">
                                Grasas Saludables
                              </span>
                              <span className="text-white font-bold" data-id="element-2279">
                                {activeIngredient.nutrients.fat}%
                              </span>
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-2280">
                              <motion.div initial={{
                              width: 0
                            }} animate={{
                              width: `${activeIngredient.nutrients.fat}%`
                            }} transition={{
                              duration: 1,
                              delay: 0.3
                            }} className="h-full bg-amber-400 rounded-full" data-id="element-2281" />
                            </div>
                          </div>

                          <div className="pt-2 border-t border-white/10 mt-2" data-id="element-2282">
                            <div className="flex justify-between text-xs" data-id="element-2283">
                              <span className="text-gray-400" data-id="element-2284">Vitaminas</span>
                              <span className="text-white font-mono" data-id="element-2285">
                                {activeIngredient.nutrients.vitamins}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </EnhancedScrollReveal>
          </div>

          {/* Right: Ingredient List */}
          <div className="lg:col-span-5 space-y-4" data-id="element-2286">
            <EnhancedScrollReveal delay={0.2} data-id="element-2287">
              <h3 className="text-lg font-bold text-charcoal-900 mb-6 px-2 flex items-center gap-2" data-id="element-2288">
                Selecciona un ingrediente{' '}
                <ArrowRight className="w-4 h-4 text-gray-400" data-id="element-2289" />
              </h3>
              <div className="space-y-3" data-id="element-2290">
                {ingredients.map((item, idx) => <button key={item.id} onClick={() => setActiveIngredient(item)} className={`w-full flex items-center gap-4 p-3 rounded-2xl transition-all duration-300 border-2 text-left group ${activeIngredient.id === item.id ? 'bg-white border-charcoal-900 shadow-xl scale-105 z-10' : 'bg-white border-transparent hover:border-gray-200 hover:shadow-md'}`} data-id="element-2291">
                    <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 shadow-sm relative" data-id="element-2292">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-2293" />
                      {activeIngredient.id === item.id && <div className="absolute inset-0 bg-charcoal-900/20" data-id="element-2294" />}
                    </div>
                    <div className="flex-1" data-id="element-2295">
                      <div className="font-bold text-charcoal-900 text-base group-hover:text-primary-700 transition-colors" data-id="element-2296">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500 font-medium uppercase tracking-wide flex items-center gap-2" data-id="element-2297">
                        {item.role}
                      </div>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${activeIngredient.id === item.id ? 'bg-charcoal-900 text-white' : 'bg-gray-50 text-gray-400'}`} data-id="element-2298">
                      <ChevronRight className="w-4 h-4" data-id="element-2299" />
                    </div>
                  </button>)}
              </div>

              <div className="mt-8 p-6 bg-green-50 rounded-3xl border border-green-100 text-center relative overflow-hidden group cursor-pointer hover:shadow-md transition-shadow" data-id="element-2300">
                <div className="absolute top-0 right-0 w-24 h-24 bg-green-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" data-id="element-2301" />
                <p className="text-green-800 font-bold mb-3 relative z-10" data-id="element-2302">
                  ¿Quieres conocer el origen de todos nuestros ingredientes?
                </p>
                <Button variant="outline" className="bg-white border-green-200 text-green-700 hover:bg-green-50 relative z-10 font-bold" data-id="element-2303">
                  Ver Mapa de Trazabilidad
                </Button>
              </div>
            </EnhancedScrollReveal>
          </div>
        </div>
      </div>
    </section>;
}